import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slider,
  Typography,
} from "@mui/material";
import { PlayArrow as RunIcon } from "@mui/icons-material";
import React, { useState } from "react";

// Discovery target: how many candidates a single run aims for. The backend
// derives its scan/query budget from this (see derive_search_budget), so
// raising it extends reach in one run instead of starting discovery repeatedly.
// Default matches the backend config default (target_candidates = 50).
const TARGET_MIN = 25;
const TARGET_MAX = 500;
const TARGET_STEP = 25;
const TARGET_DEFAULT = 50;
const TARGET_MARKS = [
  { value: 25, label: "25" },
  { value: 100, label: "100" },
  { value: 250, label: "250" },
  { value: 500, label: "500" },
];

interface DiscoveryRunModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (targetCandidates: number) => void;
}

const DiscoveryRunModal: React.FC<DiscoveryRunModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [targetCandidates, setTargetCandidates] =
    useState<number>(TARGET_DEFAULT);

  const handleConfirm = () => {
    onConfirm(targetCandidates);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { backgroundColor: "#1e1e1e", backgroundImage: "none" } }}
    >
      <DialogTitle>Run Discovery</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Choose how many candidates this run should aim for. A higher target
          searches longer and more broadly in a single run, so you don&apos;t need to start
          discovery repeatedly. Candidates already found are never surfaced
          twice.
        </Typography>
        <Box sx={{ px: 1 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Target candidates: <strong>{targetCandidates}</strong>
          </Typography>
          <Slider
            value={targetCandidates}
            onChange={(_, value) => setTargetCandidates(value as number)}
            min={TARGET_MIN}
            max={TARGET_MAX}
            step={TARGET_STEP}
            marks={TARGET_MARKS}
            valueLabelDisplay="auto"
            aria-label="Target number of candidates per discovery run"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          startIcon={<RunIcon />}
        >
          Run Discovery
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DiscoveryRunModal;
